import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CompteARebourd from '../../../composants/CompteARebourd/CompteARebourd';
import { formatStatusName } from '../../../utils/entityUtils';
import { estNumeroPanier } from '../../../utils/numeroPanier';
import { OperationMS, StatusId } from '../types';
import { computeOperationStatus } from '../domain/operationStatus';
import './OperationHeaderBanner.css';

interface OperationHeaderBannerProps {
  titre: string;
  sousTitre: string;
  subtitle: string;
  operation: OperationMS;
  backgroundImage?: string;
}

// Banniere acheteur-MS autonome (fork de SuiviHeader, branche source="MS" uniquement).
// Statut/couleur/expiration calcules PAR ID via le domaine (operationStatus), pas par nom.
// Le chrono s'affiche en consultation (statut 2), brouillon ou a_signer ; le libelle reste le nom.
const OperationHeaderBanner: React.FC<OperationHeaderBannerProps> = ({
  titre,
  sousTitre,
  subtitle,
  operation,
  backgroundImage,
}) => {
  const navigate = useNavigate();

  // L'expiration peut survenir en vivant : le chrono partage emet 'chronoExpired' a zero.
  const [chronoExpired, setChronoExpired] = useState(false);
  useEffect(() => {
    const handleChronoExpired = () => setChronoExpired(true);
    window.addEventListener('chronoExpired', handleChronoExpired);
    return () => window.removeEventListener('chronoExpired', handleChronoExpired);
  }, []);

  // Reset si l'operation change (nouvelle entite -> on recalcule l'expiration depuis ses donnees).
  useEffect(() => {
    setChronoExpired(false);
  }, [operation.id]);

  const { label, color, isExpired } = computeOperationStatus(operation, chronoExpired);
  const displayLabel = isExpired ? 'Expiré' : formatStatusName(label);

  // Encart : n° de panier (initial) + n° de marche subsequent. "pas encore" tant que le numero n'a pas
  // ete transforme (il vaut encore le numero de panier).
  // Affichage n° de panier = juste l'id de l'operation (pas le "PMS00XXX").
  const panierAffiche = operation.id != null ? String(operation.id) : '-';
  // Tant que le numero vaut encore un numero de panier (prefixe PMS) -> "Inconnu a ce stade".
  // `estNumeroPanier` teste le prefixe plutot que de comparer a `numeroPanier('MS', id)`, qui suppose
  // un padding exact a 5 chiffres. Meme detection que la banniere generique SuiviHeader.
  const marcheNumero = String(operation.numero ?? '');
  const marcheAffiche = estNumeroPanier(marcheNumero) ? 'Inconnu à ce stade' : marcheNumero;

  // Titre principal = nom de l'ouvrage (suit le buffer d'edition live, sinon la valeur persistee) ; le nom
  // de l'operation (`titre`) passe en sous-ligne. Ouvrage absent -> le nom de l'operation est le titre.
  const nomOuvrage = operation.formulaire?.nomOuvrage ?? operation.nom_ouvrage ?? '';
  const primaryTitle = nomOuvrage || titre;
  const secondaryTitle = nomOuvrage ? titre : sousTitre;

  // Chrono visible quand l'operation attend une echeance datee et n'est pas expiree.
  // Branche MS de l'oracle : status brouillon | a_signer | statut 2 (consultation en cours).
  const statusName = operation.status?.nom;
  const statusId = operation.status?.id;
  const showChrono =
    !!operation.expire_at &&
    !isExpired &&
    (statusName === 'brouillon' ||
      statusName === 'a_signer' ||
      statusId === StatusId.ConsultationEnCours);

  return (
    <div
      className="opms-banner"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${backgroundImage})`,
      }}
    >
      {/* Retour en TEXTE (style page de login) au lieu du chevron. MS = suivi d'operation acheteur
          -> "vers les commandes". Navigation inchangee (navigate(-1)), seul le visuel change. */}
      <button
        className="opms-banner__back no-print"
        onClick={() => navigate(-1)}
        aria-label="Retour vers les commandes"
      >
        ← Retour vers les commandes
      </button>

      <div className="opms-banner__row">
        {/* Encart orange (a gauche) : statut, n° de panier (initial), sensibilite, n° de marche. */}
        <aside className="opms-banner__encart no-print">
          {/* Statut : chip blanc, texte colore par statut (plus de libelle "Statut :"), tient sur une ligne. */}
          <div className="opms-banner__encart-statut" style={{ color }}>{displayLabel}</div>
          <div className="opms-banner__encart-ligne">
            <span className="opms-banner__encart-lbl">N° de panier : </span>
            <span className="opms-banner__encart-val">{panierAffiche}</span>
          </div>
          <div className="opms-banner__encart-ligne opms-banner__encart-ligne--bloc">
            <span className="opms-banner__encart-lbl">N° de marché subséquent :</span>
            <span className="opms-banner__encart-val">{marcheAffiche}</span>
          </div>
          {/* Chrono EN BAS de l'encart : blanc, sans encadrement, libelle "Temps restant :" au-dessus. */}
          {showChrono && (
            <div className="opms-banner__encart-chrono">
              <span className="opms-banner__encart-chrono-lbl">Temps restant :</span>
              <CompteARebourd targetDate={operation.expire_at as string} />
            </div>
          )}
        </aside>

        <div className="opms-banner__col">
          <h1 title={primaryTitle}>{primaryTitle}</h1>
          {secondaryTitle && <h2>{secondaryTitle}</h2>}

          <div className="opms-banner__subtitle">
            <span>{subtitle}</span>
          </div>

          {/* Sensibilite : deplacee de l'encart vers le bloc de droite, sur une ligne. */}
          {operation.niveau_sensibilite ? (
            <div className="opms-banner__sensibilite">Sensibilité : {operation.niveau_sensibilite}</div>
          ) : null}

          {/* Bloc statut de droite (.opms-banner__status-header) SUPPRIME : la pastille est desactivee et
              le chrono vit dans l'encart orange -> il ne restait qu'une pilule blanche vide (fond blanc +
              border-radius:50px). Statut/chrono sont tous les deux dans l'encart a gauche.
          <div className="opms-banner__status">
            <div className="opms-banner__status-header">
              {false && (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 45 41" fill="none">
                    <ellipse cx="22.5" cy="20.5" rx="22.5" ry="20.5" fill={color} />
                  </svg>
                  <span className="opms-banner__status-text" style={{ color }}>{displayLabel}</span>
                </>
              )}
            </div>
          </div>
          */}
        </div>
      </div>
    </div>
  );
};

export default OperationHeaderBanner;
